import { WalletError } from '@cef-ai/wallet-api-client';
import type { Identity } from '@cef-ai/wallet-identity';
import type { HostToPopup, PopupToHost } from '@cef-ai/wallet';
import type { AppContextStore, PendingRegistrationStore } from '~/stores';
import { classifyCeremonyRefusal } from './ceremonyRefusal';

export interface HelloHandlerDeps {
  identity: Identity;
  appContextStore: AppContextStore;
  /**
   * Task 22. Present on the POPUP surface only; the seam through which a
   * message handler reaches a UI.
   *
   * S0 requires a verified mailbox before the registration ceremony, and only
   * the wallet origin can mint the `emailToken` that proves it — so the
   * embedded register intent needs two screens of human input before anything
   * can be signed. This handler cannot render them, so it hands off: it calls
   * `request()` and awaits, exactly as `createRequestDelegationHandler`
   * already awaits `PendingConsentStore`, and `<RegisterPrompt>` (mounted by
   * `EmbedWalletRoute`) resolves it.
   *
   * Optional because `createIframeBridge` deliberately does NOT supply one —
   * see the `intent === 'register'` branch below.
   */
  pendingRegistrationStore?: PendingRegistrationStore;
  /**
   * S1 (wire fix). The document whose Permissions Policy is consulted to tell
   * a refused ceremony from a dismissed one — this surface's own document, and
   * on the iframe surface that means the wallet FRAME's. Optional: production
   * callers omit it and `classifyCeremonyRefusal` reads `globalThis.document`,
   * which is the same object.
   *
   * It exists as a parameter so a test can drive the real wire (frame policy
   * denies → `wallet:error` → host falls back) without stubbing the ONE global
   * that both ends of that wire would then have to share — which would make
   * the frame's policy and the host's the same answer by construction, and the
   * test unable to tell them apart.
   */
  ceremonyDocument?: unknown;
  send: (msg: PopupToHost) => void;
}

export function createHelloHandler(deps: HelloHandlerDeps) {
  return async (msg: Extract<HostToPopup, { type: 'wallet:hello' }>, meta: { origin: string }): Promise<void> => {
    deps.appContextStore.setApp(msg.appContext);
    /**
     * Task 24. Whether the branch about to run CREATES the account. Assigned
     * once, in the branch that ran, and never derived from state observed
     * afterwards: by the time this handler replies, "does an account exist?"
     * is `true` on both paths and can no longer tell them apart. Declared
     * without an initialiser on purpose — a `= false` default would let a
     * future branch forget to set it and silently report a creation as a
     * sign-in, whereas TypeScript refuses to compile a path that leaves it
     * unassigned.
     */
    let accountCreated: boolean;
    if (msg.intent === 'register') {
      const store = deps.pendingRegistrationStore;
      if (!store) {
        // No registration UI on this surface. In practice only the IFRAME
        // bridge lands here, and it is unreachable by design: Safari blocks
        // WebAuthn `create()` in a cross-origin iframe, so
        // `EmbedWallet.register()` always punches out to a popup. Reaching
        // this line therefore means the wiring is wrong, not that the reader
        // did anything — so it names the wiring. `internal` (what this threw
        // before fix round 1) settled the host's call but told it nothing:
        // "something went wrong on our side" is indistinguishable from a
        // server fault, and this is neither a server nor a fault of the
        // reader's.
        throw new WalletError(
          'registration-unsupported',
          'wallet:hello{intent:register} reached a wallet surface with no registration UI; registration runs in the popup (WebAuthn create() is blocked in cross-origin iframes)',
        );
      }
      // The mailbox is proven BEFORE the ceremony, and this await is what
      // enforces it here: the handler owns no `emailToken` and never calls
      // `identity.register()` itself, so there is no path from this message to
      // an authenticator prompt that does not go through the prompt's
      // `email/start` → `email/verify` exchange first. `completed` is the only
      // outcome that continues, and the prompt resolves it only once a session
      // exists.
      const outcome = await store.request({
        hostOrigin: meta.origin,
        appName: msg.appContext.name,
        label: msg.label,
      });
      if (outcome.kind === 'cancelled') {
        // Abandoned: closed at the address step, at the code step, or after a
        // ceremony the reader chose not to retry. Nothing was created (S0
        // verifies before it registers, so at worst an expired verification
        // row is left behind). Throwing is what gives the host a NAMED
        // `wallet:error` — `PopupHostBridge` maps a thrown `WalletError`
        // straight through — instead of a promise it would wait out.
        throw new WalletError(
          'user-cancelled',
          'the registration flow was closed before a wallet was created; no account and no credential exist',
        );
      }
      // The prompt ran either `identity.register()` or, on the
      // `already-registered` branch, `identity.login()`. It is the only place
      // that knows which, so it says so and this handler forwards it verbatim.
      accountCreated = outcome.accountCreated;
    } else {
      // Task 5 fix: when the host pins a `credentialId` (step-up of a known
      // session), forward it so `identity.login()` pins WebAuthn
      // `allowCredentials` to it — the stepping-up surface's vault is closed
      // (the iframe's, since S1; the popup's on the policy fallback), so
      // without this the browser would show the discoverable-credential
      // picker and let the user authenticate as a DIFFERENT passkey.
      //
      // S1 (wire fix). The ceremony's rejection is classified HERE, not on the
      // host, and the reason is that this is the last place the information
      // exists. `navigator.credentials.get()` rejects with a raw
      // `NotAllowedError` whether Permissions Policy refused the framed
      // ceremony or the reader dismissed the sheet; the two need opposite
      // responses from the SDK. `PopupHostBridge` turns any non-`WalletError`
      // throw into `wallet:error{code:'internal'}` and `IframeTransport`
      // rebuilds it with `name === 'WalletError'`, so `err.name` does not
      // survive the trip and a host-side test on it can never fire. A `code`
      // does survive — `createHelloHandler` already relies on that for its two
      // other throws — so the classification is expressed as one.
      try {
        await deps.identity.login({ credentialId: msg.credentialId });
      } catch (err) {
        throw classifyCeremonyRefusal(err, deps.ceremonyDocument ?? (globalThis as { document?: unknown }).document);
      }
      // `wallet:hello{intent:'login'}` has no path to `identity.register()`,
      // so this is a fact about the message type, not an observation.
      accountCreated = false;
    }
    const addresses = deps.identity.addresses!;
    const credentialId = deps.identity.credentialId!;
    // Task 20 (fix round 1). Carry the account the ceremony just
    // authenticated onto the wire, same as addresses/credentialId — this is
    // what lets `EmbedWallet.helloAndAwaitLogin` overwrite its account
    // getters on every ceremony instead of leaving a PRIOR session's account
    // stuck next to a NEW session's addresses. `identity.account` is
    // `readonly account?` on the interface (thin doubles may omit it); omit
    // the field entirely rather than sending `undefined`/`null` when absent,
    // so an adopter can tell "no account reported" from "account has no
    // email".
    const account = deps.identity.account;
    deps.send({
      type: 'wallet:login:ok',
      id: msg.id,
      addresses,
      credentialId,
      ...(account ? { account } : {}),
      // Task 24. Always sent by this (current) wallet surface, as a definite
      // `true` or `false`. Absence on the wire is reserved for a wallet
      // deployment older than the field — which is why the host side reads an
      // absent value as UNKNOWN rather than as `false`.
      accountCreated,
    });
  };
}
